import {
  ContentType,
  computeMessageGroups,
  type AgentContent,
  type ComponentContent,
  type MessageGroup,
} from '../../../lib/agent-library.ts';
import { plainTextFromMarkdown } from '../a2ui/blocks/markdown-plain.ts';
import {
  replaySurface,
  type ReducedSurface,
} from '../../../../vendor/agentplace-a2ui/surface-reduction.ts';

export interface TurnEntry {
  /** Stable unique identity of this turn's opening content item. */
  id: string;
  /** Run correlation id. Multiple turns may legitimately share it. */
  responseId?: string;
  /** Successful Surface outputs in this group's content order; the last is selected. */
  surfaceIds: string[];
  surfaceContentId?: string;
  /** The last accepted composition in this turn, independent of the mutable live surface. */
  surfaceSnapshot?: ReducedSurface;
  surfaceFallback?: string;
  /** The user's message text that opened this turn. Empty for `home` turns. */
  request: string;
  /** Cleaned final Surface text, or assistant prose for text-only turns, for the rail peek. */
  responsePreview: string;
  /** Uncapped assistant text. resolveStageView may use TextPage for settled
   *  compatibility turns without a renderable artifact; prose does not replace
   *  an existing artifact or claim the canvas while a turn is in flight. */
  responseText: string;
  /** True when the turn has no visible user request: a HIDDEN user message
   *  (the welcome scenario, `[user opened the agent]`) or, on the session's
   *  first turn, no user message at all (a restored greeting stream). The
   *  turn IS a page — the site's home — so it must exist in the index; the
   *  rail labels it "Home" instead of showing the synthetic request text. */
  home?: boolean;
  /** Channel that opened the turn, e.g. `'voice'`. Undefined for typed turns.
   *  resolveStageView keeps voice-only answers from replacing the page with
   *  compatibility text; typed prose also preserves an existing artifact. */
  channel?: string;
}

const RESPONSE_PREVIEW_MAX_CHARS = 160;

/**
 * Fold the flat message list into turn entries for the turn rail.
 *
 * A thin projection of `computeMessageGroups` — the shared run-based grouper
 * in `agent-library` — onto the rail's display shape. One `TurnEntry` per
 * group, in order.
 * `home` turns (a hidden user message, or orphan agent content on the
 * session's first turn) are kept rather than dropped: the welcome turn is
 * the site's home page, and skipping it would leave the stage with nothing
 * to display for the most important turn of the session. Every
 * non-reasoning assistant text response accumulates into `responseText`.
 * The rail preview prefers the last accepted Surface over intermediate prose;
 * its Markdown and URLs are cleaned before applying the display length cap.
 */
export function buildTurnIndex(messages: AgentContent[]): TurnEntry[] {
  return computeMessageGroups(
    messages.filter((message) => !(message.hidden && message.channel === 'voice')),
  ).map(toTurnEntry);
}

/** Spoken requests remain in conversation/activity; the rail needs a visual answer. */
export function isRailTurn(turn: TurnEntry): boolean {
  return turn.channel !== 'voice' || turn.surfaceIds.length > 0;
}

export function visibleRailTurnIndex(turns: readonly TurnEntry[], index: number): number {
  return turns.findLastIndex((turn, candidate) => candidate <= index && isRailTurn(turn));
}

export function selectedTurnIndex(
  turns: readonly TurnEntry[],
  selectedId: string | null,
): number | null {
  const index = turns.findIndex((turn) => turn.id === selectedId);
  return index < 0 ? null : index;
}

function toTurnEntry(group: MessageGroup): TurnEntry {
  const entry: TurnEntry = {
    id: turnIdentity(group),
    responseId: group.responseId,
    surfaceIds: [],
    request: visibleRequestText(group.request),
    responsePreview: '',
    responseText: '',
    home: group.kind === 'home' ? true : undefined,
    channel: group.request?.channel,
  };

  for (const response of group.responses) {
    accumulateResponse(entry, response);
  }

  const surface = group.responses.findLast(isSettledSurface);
  const previewSource = surface
    ? surface.fallbackText?.trim() || surface.fallbackMarkdown || ''
    : entry.responseText;
  entry.responsePreview = plainTextFromMarkdown(previewSource)
    .replace(/^\s{0,3}#{1,6}\s*$/gm, '')
    .replace(/<?https?:\/\/[^\s<>]+>?/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, RESPONSE_PREVIEW_MAX_CHARS);

  return entry;
}

function turnIdentity(group: MessageGroup): string {
  return group.request?.messageId ?? group.responses[0]?.messageId ?? group.id;
}

function visibleRequestText(request: AgentContent | null): string {
  if (!request || request.hidden || request.type !== ContentType.Text) {
    return '';
  }
  return request.content;
}

function accumulateResponse(entry: TurnEntry, message: AgentContent): void {
  if (isSettledSurface(message) && typeof message.props.surfaceId === 'string') {
    entry.surfaceIds = entry.surfaceIds.filter((id) => id !== message.props.surfaceId);
    entry.surfaceIds.push(message.props.surfaceId);
    entry.surfaceContentId = message.messageId;
    entry.surfaceSnapshot =
      replaySurface(message.props.surfaceId, message.props.surfaceReplay) ?? undefined;
    entry.surfaceFallback = message.fallbackText ?? message.fallbackMarkdown;
  }
  if (message.type !== ContentType.Text || message.isReasoning || message.hidden) {
    return;
  }

  entry.responseText = `${entry.responseText} ${message.content}`.trim();
}

function isSettledSurface(message: AgentContent): message is ComponentContent {
  return (
    message.type === ContentType.Component &&
    message.componentName === 'Surface' &&
    !message.hidden &&
    (message.streaming?.state === 'output-available' ||
      (!message.streaming && Array.isArray(message.props.surfaceReplay)))
  );
}
